import { Button, Stack } from '@mantine/core';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import React, { FC, FormEvent, ReactNode } from 'react';
import styled from 'styled-components';

import { useWizard } from '@portals/framework';

import { OnboardingWizardContextDataType, StepId } from './types';

interface DetailsFormProps {
  title: string;
  description: ReactNode;
  onNextClick: () => void;
  onPrevClick?: () => void;
  nextButtonText?: string;
  prevButtonText?: string;
  className?: string;
  isLoading?: boolean;
}

const DetailsForm: FC<DetailsFormProps> = ({
  title,
  description,
  onNextClick,
  onPrevClick,
  nextButtonText = 'Save and continue',
  prevButtonText = 'Go to previous step',
  className,
  children,
  isLoading = false,
}) => {
  const { isNextDisabled, isPrevDisabled, isInTransition } = useWizard<
    OnboardingWizardContextDataType,
    StepId
  >();

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onNextClick();
  };

  return (
    <Form
      initial={{ x: -100, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
      onSubmit={onSubmit}
      className={classNames('mb-4', className)}
    >
      <Title>{title}</Title>
      <Description>{description}</Description>

      {children}

      <Stack sx={{ maxWidth: 370 }} mt={40}>
        <Button
          sx={{ width: '100%' }}
          type="submit"
          size="md"
          disabled={isNextDisabled || isLoading}
          loading={isLoading || isInTransition}
        >
          {nextButtonText}
        </Button>

        {onPrevClick && (
          <Button
            sx={{ width: '100%' }}
            type="button"
            variant="default"
            color="gray"
            size="md"
            onClick={onPrevClick}
            disabled={isPrevDisabled}
          >
            {prevButtonText}
          </Button>
        )}
      </Stack>
    </Form>
  );
};

const Form = styled(motion.form)`
  max-width: 370px;
`;

const Title = styled.h1`
  margin-bottom: 12px;
  font-weight: 700;
  font-size: 32px;
  line-height: 1.25;
`;

const Description = styled.p`
  margin-bottom: 40px;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #6a7073;
`;

export default DetailsForm;
